/* Three.js globe — Origin section.
 * Hairline lat/lon grid sphere with city pins and trade-route arcs
 * converging on Carlton (Melbourne) from 6 European partners.
 *
 * City labels live in an HTML overlay, not 3D, so they always read
 * horizontally and avoid each other in screen-space regardless of rotation.
 * Auto-rotates; pointer drag spins manually (resumes auto after release).
 * Tokens (canvas / ink / accent) read from CSS so the globe flips on dark mode.
 */
import * as THREE from 'three';

const CITIES = [
  { name: 'Antwerp', lat: 51.219, lon: 4.402,   type: 'eu' },
  { name: 'Munich',  lat: 48.137, lon: 11.575,  type: 'eu' },
  { name: 'Treviso', lat: 45.667, lon: 12.243,  type: 'eu' },
  { name: 'Verona',  lat: 45.439, lon: 10.998,  type: 'eu' },
  { name: 'Modena',  lat: 44.647, lon: 10.925,  type: 'eu' },
  { name: 'Athens',  lat: 37.983, lon: 23.728,  type: 'eu' },
  { name: 'Carlton', lat: -37.8,  lon: 144.97,  type: 'home' },
];

// Preferred screen-space directions for label placement, tried in order
const LABEL_ANGLES = [-90, -60, -120, -30, -150, 30, 150, 0, 180, 60, 120, 90];
// Base distances tuned for a 511px-tall canvas; scaled proportionally per-frame
const LABEL_DISTS_BASE = [44, 60, 80, 110, 145];
// Reference canvas height for distance scaling
const LABEL_DIST_REF_H = 511;

function latLonToVec3(lat, lon, r = 1) {
  const phi   = (90 - lat) * Math.PI / 180;
  const theta = (lon + 180) * Math.PI / 180;
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
     r * Math.cos(phi),
     r * Math.sin(phi) * Math.sin(theta),
  );
}

function readToken(name, fallback) {
  const v = getComputedStyle(document.body).getPropertyValue(name).trim();
  return v || fallback;
}

function hexToInt(hex) {
  return parseInt(hex.replace('#', '').slice(0, 6), 16);
}

function rectsIntersect(a, b, pad = 4) {
  return !(a.x + a.w + pad < b.x || b.x + b.w + pad < a.x ||
           a.y + a.h + pad < b.y || b.y + b.h + pad < a.y);
}

export function initGlobe(canvas) {
  if (!canvas || canvas.dataset.globeInit === '1') return;
  canvas.dataset.globeInit = '1';

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    preserveDrawingBuffer: true,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setClearColor(0x000000, 0);

  const scene  = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100);
  camera.position.set(0, 0, 4.2);

  const group = new THREE.Group();
  scene.add(group);

  // Resolve theme tokens
  const palette = {
    ink:    hexToInt(readToken('--ink', '#131211')),
    canvas: hexToInt(readToken('--canvas', '#FAFAF7')),
    accent: hexToInt(readToken('--accent', '#6F7C8C')),
  };

  const mats = {
    parallel: new THREE.LineBasicMaterial({ color: palette.ink, transparent: true, opacity: 0.45 }),
    meridian: new THREE.LineBasicMaterial({ color: palette.ink, transparent: true, opacity: 0.45 }),
    equator:  new THREE.LineBasicMaterial({ color: palette.ink, transparent: true, opacity: 0.95 }),
    prime:    new THREE.LineBasicMaterial({ color: palette.ink, transparent: true, opacity: 0.85 }),
    pole:     new THREE.LineBasicMaterial({ color: palette.ink, transparent: true, opacity: 1.0 }),
    arc:      new THREE.LineBasicMaterial({ color: palette.accent, transparent: true, opacity: 0.95 }),
    arcDim:   new THREE.LineBasicMaterial({ color: palette.accent, transparent: true, opacity: 0.30 }),
    pin:      new THREE.MeshBasicMaterial({ color: palette.ink }),
    pinHome:  new THREE.MeshBasicMaterial({ color: palette.accent }),
    pinRing:  new THREE.LineBasicMaterial({ color: palette.ink, transparent: true, opacity: 1.0 }),
    pulse:    new THREE.LineBasicMaterial({ color: palette.accent, transparent: true, opacity: 1.0 }),
    halo:     new THREE.MeshBasicMaterial({ color: palette.ink, transparent: true, opacity: 0.06, side: THREE.BackSide }),
    head:     new THREE.MeshBasicMaterial({ color: palette.accent }),
  };

  // ── Z-buffer occluder so far-side pins hide behind the sphere ──
  const occluder = new THREE.Mesh(
    new THREE.SphereGeometry(0.985, 64, 48),
    new THREE.MeshBasicMaterial({ colorWrite: false }),
  );
  group.add(occluder);

  // ── Atmosphere halo ──
  const halo = new THREE.Mesh(
    new THREE.SphereGeometry(1.12, 48, 32),
    mats.halo,
  );
  group.add(halo);

  // ── Lat/lon grid ──
  for (let lat = -75; lat <= 75; lat += 15) {
    const isEquator = lat === 0;
    const pts = [];
    const phi = (90 - lat) * Math.PI / 180;
    const r = Math.sin(phi);
    const y = Math.cos(phi);
    for (let i = 0; i <= 96; i++) {
      const t = (i / 96) * Math.PI * 2;
      pts.push(new THREE.Vector3(r * Math.cos(t), y, r * Math.sin(t)));
    }
    const g = new THREE.BufferGeometry().setFromPoints(pts);
    group.add(new THREE.Line(g, isEquator ? mats.equator : mats.parallel));
  }
  for (let lon = 0; lon < 360; lon += 15) {
    const isPrime = lon === 0;
    const pts = [];
    for (let i = 0; i <= 96; i++) {
      const lat = -90 + (i / 96) * 180;
      pts.push(latLonToVec3(lat, lon, 1));
    }
    const g = new THREE.BufferGeometry().setFromPoints(pts);
    group.add(new THREE.Line(g, isPrime ? mats.prime : mats.meridian));
  }

  const polarPts = [
    new THREE.Vector3(0, 1.04, 0), new THREE.Vector3(0, 1.16, 0),
    new THREE.Vector3(0, -1.04, 0), new THREE.Vector3(0, -1.16, 0),
  ];
  group.add(new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(polarPts), mats.pole));

  // ── HTML overlay for labels ──
  // Mounted as sibling of the canvas — relies on .map-frame being position:relative
  const parent = canvas.parentElement;
  const mountStyle = getComputedStyle(parent).position;
  if (mountStyle === 'static') parent.style.position = 'relative';
  let overlay = parent.querySelector('.globe-label-overlay');
  if (overlay) overlay.remove(); // hot reload safety
  overlay = document.createElement('div');
  overlay.className = 'globe-label-overlay';
  overlay.style.cssText = [
    'position:absolute',
    'inset:0',
    'pointer-events:none',
    'overflow:visible',
    'z-index:2',
  ].join(';');
  parent.appendChild(overlay);

  // SVG overlay for leader lines (one polyline per city, updated each frame)
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.style.cssText = 'position:absolute;inset:0;pointer-events:none;overflow:visible';
  overlay.appendChild(svg);

  // ── Pin markers + label DOM nodes ──
  const cityNodes = []; // { city, pos: Vec3 (sphere unit), normal, isHome, labelEl, leaderEl }
  let homeNormal = null;

  CITIES.forEach((city) => {
    const pos = latLonToVec3(city.lat, city.lon, 1.0);
    const isHome = city.type === 'home';
    const pinSize = isHome ? 0.044 : 0.024;

    const normal = pos.clone().normalize();
    const tangent = new THREE.Vector3()
      .crossVectors(normal, Math.abs(normal.y) > 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0))
      .normalize();
    const bitangent = new THREE.Vector3().crossVectors(normal, tangent).normalize();

    // Solid pin dot
    const dot = new THREE.Mesh(
      new THREE.SphereGeometry(pinSize, 16, 16),
      isHome ? mats.pinHome : mats.pin,
    );
    dot.position.copy(pos.clone().multiplyScalar(1.014));
    group.add(dot);

    // Hairline ring
    const ringR = isHome ? 0.085 : 0.05;
    const ringPts = [];
    for (let i = 0; i <= 48; i++) {
      const t = (i / 48) * Math.PI * 2;
      const offset = tangent.clone().multiplyScalar(Math.cos(t) * ringR)
        .add(bitangent.clone().multiplyScalar(Math.sin(t) * ringR));
      ringPts.push(pos.clone().multiplyScalar(1.018).add(offset));
    }
    group.add(new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(ringPts),
      mats.pinRing,
    ));

    if (isHome) {
      homeNormal = normal.clone();
      const pulsePts = [];
      const pulseR = 0.13;
      for (let i = 0; i <= 64; i++) {
        const t = (i / 64) * Math.PI * 2;
        const offset = tangent.clone().multiplyScalar(Math.cos(t) * pulseR)
          .add(bitangent.clone().multiplyScalar(Math.sin(t) * pulseR));
        pulsePts.push(pos.clone().multiplyScalar(1.020).add(offset));
      }
      const pulseGeom = new THREE.BufferGeometry().setFromPoints(pulsePts);
      const pulseLine = new THREE.Line(pulseGeom, mats.pulse);
      group.add(pulseLine);
      group.userData.pulseLine = pulseLine;
    }

    // HTML label
    const labelEl = document.createElement('div');
    labelEl.className = 'globe-label' + (isHome ? ' is-home' : '');
    labelEl.textContent = city.name.toUpperCase();
    labelEl.style.cssText = [
      'position:absolute',
      'left:0',
      'top:0',
      'transform:translate(-50%, -50%)',
      'will-change:transform,opacity',
      'font:11px/1 ui-monospace, "SF Mono", Menlo, monospace',
      'letter-spacing:0.06em',
      `color:${isHome ? 'var(--accent, #6F7C8C)' : 'var(--ink, #FAFAF7)'}`,
      'white-space:nowrap',
      'opacity:0',
      'transition:opacity 220ms ease',
      'text-shadow:0 0 4px rgba(0,0,0,0.6)',
    ].join(';');
    overlay.appendChild(labelEl);

    const leaderEl = document.createElementNS(svgNS, 'line');
    leaderEl.setAttribute('stroke', isHome ? 'var(--accent, #6F7C8C)' : 'var(--ink, #FAFAF7)');
    leaderEl.setAttribute('stroke-width', '1');
    leaderEl.setAttribute('stroke-opacity', '0.5');
    leaderEl.setAttribute('vector-effect', 'non-scaling-stroke');
    svg.appendChild(leaderEl);

    cityNodes.push({ city, pos: pos.clone(), normal, isHome, labelEl, leaderEl });
  });

  // ── Great-circle arcs ──
  const arcs = [];
  const carltonPos = latLonToVec3(-37.8, 144.97, 1.0);
  CITIES.filter(c => c.type === 'eu').forEach((city) => {
    const start = latLonToVec3(city.lat, city.lon, 1.0);
    const end   = carltonPos;
    const segments = 96;
    const pts = [];
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const omega = Math.acos(Math.min(1, Math.max(-1, start.dot(end))));
      const sinO = Math.sin(omega);
      const a = Math.sin((1 - t) * omega) / sinO;
      const b = Math.sin(t * omega) / sinO;
      const slerp = new THREE.Vector3()
        .copy(start).multiplyScalar(a)
        .add(end.clone().multiplyScalar(b));
      const lift = 1.0 + Math.sin(t * Math.PI) * 0.22;
      pts.push(slerp.multiplyScalar(lift));
    }
    const fullGeom = new THREE.BufferGeometry().setFromPoints(pts);
    group.add(new THREE.Line(fullGeom, mats.arcDim));

    const headGeom = new THREE.BufferGeometry();
    headGeom.setAttribute('position', new THREE.BufferAttribute(new Float32Array((segments + 1) * 3), 3));
    headGeom.setDrawRange(0, 0);
    const headLine = new THREE.Line(headGeom, mats.arc);
    group.add(headLine);

    const headDot = new THREE.Mesh(new THREE.SphereGeometry(0.018, 12, 12), mats.head);
    headDot.visible = false;
    group.add(headDot);

    arcs.push({ pts, headGeom, headLine, headDot, segments });
  });

  // Initial framing — Europe centered, slight northward tilt
  group.rotation.x = -0.32;
  group.rotation.y = -1.55;

  // ── Render loop ──
  let isPaused = false;
  let resumeTimer = 0;
  const baseSpeed = 0.0016;
  let lastTs = performance.now();

  function resize() {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (w === 0 || h === 0) return;
    const dpr = renderer.getPixelRatio();
    if (canvas.width !== Math.floor(w * dpr) || canvas.height !== Math.floor(h * dpr)) {
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
  }
  resize();
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(resize).observe(canvas);
  }
  window.addEventListener('resize', resize);

  // Project a world-space Vec3 to canvas-relative pixel coords
  // Returns { x, y, behind: boolean (z > 1 means behind camera, in NDC)}
  const _v = new THREE.Vector3();
  function projectToScreen(worldVec) {
    _v.copy(worldVec).project(camera);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    return {
      x: (_v.x * 0.5 + 0.5) * w,
      y: (-_v.y * 0.5 + 0.5) * h,
      behind: _v.z > 1,
    };
  }

  function placeLabels() {
    const camDir = new THREE.Vector3(0, 0, 1);
    const groupQuat = group.quaternion;
    const placed = []; // {x,y,w,h} bboxes already taken
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    // Scale label distances to canvas size — keeps offsets readable on mobile
    const distScale = Math.max(0.55, h / LABEL_DIST_REF_H);
    const dists = LABEL_DISTS_BASE.map((d) => d * distScale);
    // Allow labels to spill into the .map-frame padding around the canvas (≈14px on mobile)
    // by widening the placement bounds beyond the canvas
    const overflow = 14;
    const minX = -overflow;
    const minY = -overflow;
    const maxX = w + overflow;
    const maxY = h + overflow;

    // Sort by z-depth so closest-to-camera labels get priority
    const sorted = cityNodes.map((n) => {
      const worldPos = n.pos.clone().applyQuaternion(groupQuat);
      const facing = n.normal.clone().applyQuaternion(groupQuat).dot(camDir);
      return { n, worldPos, facing };
    }).sort((a, b) => b.facing - a.facing);

    sorted.forEach(({ n, worldPos, facing }) => {
      const screen = projectToScreen(worldPos);
      const visible = facing > 0.10 && !screen.behind;

      if (!visible) {
        n.labelEl.style.opacity = '0';
        n.leaderEl.setAttribute('stroke-opacity', '0');
        return;
      }

      // Measure label
      const lw = n.labelEl.offsetWidth || 60;
      const lh = n.labelEl.offsetHeight || 12;

      // Pin bbox added to placed list so labels avoid sitting on pins
      // (only do this once, when first city processed)

      // Try angles × distances, pick first non-colliding
      let chosen = null;
      for (const dist of dists) {
        for (const angle of LABEL_ANGLES) {
          const a = (angle * Math.PI) / 180;
          const cx = screen.x + Math.cos(a) * dist;
          const cy = screen.y + Math.sin(a) * dist;
          const x = cx - lw / 2;
          const y = cy - lh / 2;
          // Keep label inside placement bounds (canvas + overflow padding)
          if (x < minX || y < minY || x + lw > maxX || y + lh > maxY) continue;
          const rect = { x, y, w: lw, h: lh };
          // Avoid existing labels and pin centers
          let collides = placed.some((p) => rectsIntersect(p, rect, 6));
          if (!collides) {
            // Also avoid landing on top of any other city pin
            for (const o of cityNodes) {
              if (o === n) continue;
              const op = projectToScreen(o.pos.clone().applyQuaternion(groupQuat));
              const oFacing = o.normal.clone().applyQuaternion(groupQuat).dot(camDir);
              if (oFacing < 0.05) continue;
              const pinRect = { x: op.x - 9, y: op.y - 9, w: 18, h: 18 };
              if (rectsIntersect(pinRect, rect, 4)) { collides = true; break; }
            }
          }
          if (!collides) {
            chosen = { rect, cx, cy };
            break;
          }
        }
        if (chosen) break;
      }

      if (!chosen) {
        // Fallback: directly above pin
        const x = screen.x - lw / 2;
        const y = screen.y - 24 - lh / 2;
        chosen = { rect: { x, y, w: lw, h: lh }, cx: screen.x, cy: screen.y - 24 };
      }

      placed.push(chosen.rect);

      // Position label
      n.labelEl.style.transform = `translate(${chosen.cx}px, ${chosen.cy}px) translate(-50%, -50%)`;
      const targetOpacity = Math.min(1, (facing - 0.05) * 6);
      n.labelEl.style.opacity = String(targetOpacity);

      // Leader line: from near-pin (12px from pin in label direction) to label edge
      const dx = chosen.cx - screen.x;
      const dy = chosen.cy - screen.y;
      const dlen = Math.hypot(dx, dy) || 1;
      const ux = dx / dlen, uy = dy / dlen;
      const startX = screen.x + ux * 9;
      const startY = screen.y + uy * 9;
      // End at edge of label rect along the same direction
      const endX = chosen.cx - ux * (lw / 2 + 3);
      const endY = chosen.cy - uy * (lh / 2 + 3);
      n.leaderEl.setAttribute('x1', String(startX));
      n.leaderEl.setAttribute('y1', String(startY));
      n.leaderEl.setAttribute('x2', String(endX));
      n.leaderEl.setAttribute('y2', String(endY));
      n.leaderEl.setAttribute('stroke-opacity', String(targetOpacity * 0.5));
    });
  }

  function updateArcs(now) {
    const cycleMs = 5400;
    arcs.forEach((arc, i) => {
      const stagger = (i / arcs.length) * cycleMs * 0.6;
      const phase = ((now + stagger) % cycleMs) / cycleMs;
      const headLen = 0.22;
      const headEnd = phase;
      const headStart = Math.max(0, headEnd - headLen);
      const startSeg = Math.floor(headStart * arc.segments);
      const endSeg   = Math.min(arc.segments, Math.ceil(headEnd * arc.segments));
      const count    = Math.max(0, endSeg - startSeg + 1);

      const positions = arc.headGeom.attributes.position.array;
      for (let s = 0; s <= count; s++) {
        const idx = startSeg + s;
        if (idx >= arc.pts.length) break;
        const p = arc.pts[idx];
        positions[s * 3 + 0] = p.x;
        positions[s * 3 + 1] = p.y;
        positions[s * 3 + 2] = p.z;
      }
      arc.headGeom.setDrawRange(0, count);
      arc.headGeom.attributes.position.needsUpdate = true;

      if (count > 0 && phase < 0.97) {
        const headIdx = Math.min(arc.pts.length - 1, endSeg);
        arc.headDot.position.copy(arc.pts[headIdx]);
        arc.headDot.visible = true;
      } else {
        arc.headDot.visible = false;
      }
    });
  }

  function updatePulse(now) {
    const pl = group.userData.pulseLine;
    if (!pl || !homeNormal) return;
    const camDir = new THREE.Vector3(0, 0, 1);
    const worldNormal = homeNormal.clone().applyQuaternion(group.quaternion);
    const facing = worldNormal.dot(camDir);
    if (facing < 0.05) {
      pl.visible = false;
      return;
    }
    pl.visible = true;
    const t = (now % 2400) / 2400;
    const eased = 1 - Math.pow(1 - t, 2);
    const scale = 1 + eased * 0.45;
    pl.scale.setScalar(scale);
    pl.material.opacity = (1 - eased) * 0.85 * Math.min(1, facing * 4);
  }

  function animate(now) {
    resize();
    const dt = (now - lastTs) / 16.6;
    lastTs = now;

    if (!isPaused) {
      group.rotation.y += baseSpeed * dt;
    } else if (resumeTimer > 0) {
      resumeTimer -= dt * 16.6;
      if (resumeTimer <= 0) isPaused = false;
    }

    updateArcs(now);
    updatePulse(now);

    renderer.render(scene, camera);
    placeLabels();

    requestAnimationFrame(animate);
  }
  requestAnimationFrame(animate);

  // ── Drag to spin ──
  let isDragging = false;
  let pointerId = null;
  let lastX = 0, lastY = 0;

  canvas.addEventListener('pointerdown', (e) => {
    isDragging = true;
    isPaused = true;
    pointerId = e.pointerId;
    lastX = e.clientX;
    lastY = e.clientY;
    canvas.setPointerCapture(e.pointerId);
  });
  canvas.addEventListener('pointermove', (e) => {
    if (!isDragging || e.pointerId !== pointerId) return;
    const dx = e.clientX - lastX;
    const dy = e.clientY - lastY;
    group.rotation.y += dx * 0.0055;
    group.rotation.x = Math.max(-1.1, Math.min(1.1, group.rotation.x + dy * 0.0055));
    lastX = e.clientX;
    lastY = e.clientY;
  });
  const releasePointer = (e) => {
    if (e && e.pointerId !== pointerId) return;
    if (!isDragging) return;
    isDragging = false;
    pointerId = null;
    resumeTimer = 1800;
  };
  canvas.addEventListener('pointerup', releasePointer);
  canvas.addEventListener('pointercancel', releasePointer);
  canvas.addEventListener('pointerleave', releasePointer);

  // ── Theme flip ──
  const repaint = () => {
    palette.ink    = hexToInt(readToken('--ink',    '#131211'));
    palette.canvas = hexToInt(readToken('--canvas', '#FAFAF7'));
    palette.accent = hexToInt(readToken('--accent', '#6F7C8C'));
    mats.parallel.color.setHex(palette.ink);
    mats.meridian.color.setHex(palette.ink);
    mats.equator.color.setHex(palette.ink);
    mats.prime.color.setHex(palette.ink);
    mats.pole.color.setHex(palette.ink);
    mats.arc.color.setHex(palette.accent);
    mats.arcDim.color.setHex(palette.accent);
    mats.pin.color.setHex(palette.ink);
    mats.pinHome.color.setHex(palette.accent);
    mats.pinRing.color.setHex(palette.ink);
    mats.pulse.color.setHex(palette.accent);
    mats.halo.color.setHex(palette.ink);
    mats.head.color.setHex(palette.accent);
    // HTML labels use CSS custom-property colors so no manual repaint needed
  };
  const themeObs = new MutationObserver(repaint);
  themeObs.observe(document.body, { attributes: true, attributeFilter: ['class'] });

  return {
    dispose: () => {
      themeObs.disconnect();
      overlay.remove();
    },
  };
}
